import { fieldLabel } from '@scrambl/shared'

/**
 * The compact field list a confirmation or plan card shows under a step
 * (Benny UX pass 3): each argument with a plain label, ids replaced by the
 * names the server resolved or dropped, never shown raw.
 */
export interface CardField {
  label: string
  value: string
}

const UUID_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

const isUuid = (value: unknown): value is string =>
  typeof value === 'string' && UUID_PATTERN.test(value)

function scalar(
  value: unknown,
  resolvedNames: Record<string, string>,
): string | null {
  if (value === null || value === undefined || value === '') return null
  if (isUuid(value)) return resolvedNames[value] ?? null
  if (typeof value === 'string') return value
  if (typeof value === 'number') return String(value)
  if (typeof value === 'boolean') return value ? 'Yes' : 'No'
  return null
}

export function describeArgs(
  args: unknown,
  resolvedNames: Record<string, string> = {},
  options: { skip?: string[] } = {},
): CardField[] {
  if (!args || typeof args !== 'object' || Array.isArray(args)) return []
  const skip = new Set(options.skip ?? [])
  const fields: CardField[] = []
  for (const [key, raw] of Object.entries(args as Record<string, unknown>)) {
    if (skip.has(key)) continue
    const label = fieldLabel(key)
    if (!label) continue
    let value: string | null = null
    if (Array.isArray(raw)) {
      const parts = raw
        .map(entry => scalar(entry, resolvedNames))
        .filter((entry): entry is string => !!entry)
      value = parts.length ? parts.join(', ') : null
    } else {
      value = scalar(raw, resolvedNames)
    }
    if (value) fields.push({ label, value })
  }
  return fields
}
